import React, { useEffect, useState } from "react";
import {
  Button,
  createStyles,
  Grid,
  makeStyles,
  Tooltip,
  Typography,
  Box,
  useTheme,
} from "@material-ui/core";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import ArrowBack from "@material-ui/icons/ArrowBack";
import ArrowDownward from "@material-ui/icons/ArrowDropUp";
import ArrowUpward from "@material-ui/icons/ArrowDropDown";
import CardCommon from "../../../../components/card/CardCommon";
import { convertDateTimeFormat } from "../../../../utils/ConvertDateTimeFormat";
import WithLoading from "../../../../utils/WithLoading";
import configChange from "../../../../assets/images/logos/logType/config-change.png";
import kdsBump from "../../../../assets/images/logos/logType/kds-bump.png";
import kdsRecall from "../../../../assets/images/logos/logType/kds-recall.png";
import kdsPriority from "../../../../assets/images/logos/logType/kds-priority.png";
import kdsIn from "../../../../assets/images/logos/logType/kds-in.png";
import discount from "../../../../assets/images/logos/logType/discount.png";
import log from "../../../../assets/images/logos/logType/log.png";
import saleComplete from "../../../../assets/images/logos/logType/sale-complete.png";
import saleCreated from "../../../../assets/images/logos/logType/sale-created.png";
import saleItem from "../../../../assets/images/logos/logType/sale-item.png";
import voidImage from "../../../../assets/images/logos/logType/void.png";
import cardPayError from "../../../../assets/images/logos/logType/card-pay-error.png";
import cardPay from "../../../../assets/images/logos/logType/card-pay.png";
import linkPay from "../../../../assets/images/logos/logType/link-pay.png";
import kitchenPrintError from "../../../../assets/images/logos/logType/kitchen-print-error.png";
import kitchenPrint from "../../../../assets/images/logos/logType/kitchen-print.png";
import kitchenPrintSuccess from "../../../../assets/images/logos/logType/kitchen-print-success.png";
import shift from "../../../../assets/images/logos/logType/shift.png";
import onlineOrders from "../../../../assets/images/logos/logType/online-orders.png";
import { CustomTheme } from "../../../../types/customTheme";
import LogDetailsModal from "../LogsPage/LogDetailsModal";

export interface VoidSummeryInfoNodeProps {
  logsNode: any;
  userDetails: any;
  isLoading: boolean;
  locationSelectorList: any;
  isGeneric: any;
  isOrderLogs: any;
  handleOrderKeyClick: any;
}

interface RouteParams {
  locationId: string;
}

const useStyles = makeStyles((theme: any) =>
  createStyles({
    root: {
      minHeight: "48px",
      width: "100%",
      margin: "0px",
      padding: "0px",
      color: "white",
    },
    backButton: {
      marginBottom: "16px",
      textTransform: "none",
      color: "black",
      borderRadius: "10px",
    },
  }),
);

const LogDetailsInfo: React.FunctionComponent<VoidSummeryInfoNodeProps> = ({
  logsNode,
  userDetails,
  isLoading,
  locationSelectorList,
  isGeneric,
  isOrderLogs,
  handleOrderKeyClick,
}) => {
  const [flatLogs, setFlatLogs] = useState<any[]>([]);
  const [selectedLogDetails, setSelectedLogDetails] = useState<any>({});
  const [isOpenLogDetailsModal, setIsOpenLogDetailsModal] = useState(false);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [userName, setUserName] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  const history = useHistory();
  const { locationId } = useParams<RouteParams>();
  const classes = useStyles();

  const sortLogs = (logs: any[], order: "asc" | "desc") => {
    return [...logs].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return order === "asc" ? dateA - dateB : dateB - dateA;
    });
  };

  useEffect(() => {
    if (!logsNode) {
      setFlatLogs([]);
      return;
    }

    const result: any[] = [];

    const isObject = (item: unknown): item is Record<string, unknown> =>
      typeof item === "object" && item !== null && !Array.isArray(item);

    for (const logs of logsNode) {
      for (const inner of Object.values(logs as Record<string, any>)) {
        for (const item of Object.values(inner as Record<string, any>)) {
          if (isObject(item)) {
            result.push(item);
          }
        }
      }
    }

    setFlatLogs(sortLogs(result, sortOrder));
  }, [logsNode, sortOrder]);

  // Check if we're viewing a specific order (has orderId in URL)
  useEffect(() => {
    const pathParts = window.location.pathname.split("/");
    const lastPart = pathParts[pathParts.length - 1];
    const hasOrderId = /^\d+_\d+$/.test(lastPart);
    setShowOrderDetails(hasOrderId);
  }, [window.location.pathname]);

  const handleOpenVLogsDetailsModal = (logDetails: any) => {
    setSelectedLogDetails(logDetails);
    setUserName(userDetails?.[logDetails.userId]?.name || logDetails?.userId);
    setIsOpenLogDetailsModal(true);
  };

  const handleBackToLogs = () => {
    // Navigate back to the logs page without the orderId
    history.push(`/${locationId}/logs`);
  };

  const getLogTypeIcon = (type: string) => {
    if (type === "void") {
      return voidImage;
    } else if (type === "discount") {
      return discount;
    } else if (type === "sale-created") {
      return saleCreated;
    } else if (type === "sale-complete") {
      return saleComplete;
    } else if (type === "sale-item") {
      return saleItem;
    } else if (type === "config-change") {
      return configChange;
    } else if (type === "kds-bump") {
      return kdsBump;
    } else if (type === "kds-recall") {
      return kdsRecall;
    } else if (type === "kds-priority") {
      return kdsPriority;
    } else if (type === "kds-in") {
      return kdsIn;
    } else if (type === "card-pay-error") {
      return cardPayError;
    } else if (type === "card-pay") {
      return cardPay;
    } else if (type === "link-pay") {
      return linkPay;
    } else if (type === "kitchen-print-error") {
      return kitchenPrintError;
    } else if (type === "kitchen-print" || type === "receipt-print") {
      return kitchenPrint;
    } else if (type === "kitchen-print-success") {
      return kitchenPrintSuccess;
    } else if (type === "shift") {
      return shift;
    } else if (type === "online-orders") {
      return onlineOrders;
    } else {
      return log;
    }
  };
  const theme: CustomTheme = useTheme();
  return (
    <>
      {/* Back Button - Show when viewing a specific order */}
      {showOrderDetails && (
        <Box mb={2} mt={2} style={{ display: "flex", justifyContent: "flex-start" }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToLogs}
            variant="outlined"
            size="small"
            style={{ backgroundColor: theme.palette.custom.yellow.main }}
            className={classes.backButton}
          >
            Back to Logs
          </Button>
        </Box>
      )}

      {/* Logs Table - Show always */}
      <Grid container>
        <Grid item xs={12}>
          {flatLogs && flatLogs.length > 0 && (
            <CardCommon
              backgroundColor={"table_header_background"}
              style={{  minHeight: "48px" }}
            >
              <Grid container style={{ display: "flex" }}>
                <Grid
                  item
                  xs={2}
                  style={{
                    paddingLeft: "12px",
                    paddingTop: "10px",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Typography align="left">Date</Typography>
                    <Button
                      onClick={() =>
                        setSortOrder((prev) =>
                          prev === "asc" ? "desc" : "asc",
                        )
                      }
                      size="small"
                      style={{ minWidth: 0, padding: 0 }}
                    >
                      {sortOrder === "asc" ? (
                        <ArrowDownward fontSize="small" />
                      ) : (
                        <ArrowUpward fontSize="small" />
                      )}
                    </Button>
                  </div>
                </Grid>

                <Grid
                  item
                  xs={2}
                  style={{
                    paddingTop: "10px",
                    textAlign: "left",
                  }}
                >
                  <Typography>Device</Typography>
                </Grid>
                <Grid
                  item
                  xs={2}
                  style={{
                    paddingTop: "10px",
                    textAlign: "left",
                  }}
                >
                  <Typography>User</Typography>
                </Grid>
                <Grid
                  item
                  xs={1}
                  style={{
                    paddingTop: "10px",
                    textAlign: "left",
                  }}
                >
                  <Typography>Type</Typography>
                </Grid>
                <Grid
                  item
                  xs={5}
                  style={{
                    paddingTop: "10px",
                    textAlign: "left",
                  }}
                >
                  <Typography>Message</Typography>
                </Grid>
              </Grid>
            </CardCommon>
          )}

          {flatLogs.map((data1, index) => (
            <CardCommon
              key={`${data1.date}-${data1.deviceId}-${index}`}
              backgroundColor={"entity_background"}
              style={{ marginTop: "4px", minHeight: "48px" }}
            >
              <Button
                className={classes.root}
                onClick={() => handleOpenVLogsDetailsModal(data1)}
              >
                <Grid container style={{ display: "flex" }}>
                  <Grid
                    item
                    xs={2}
                    style={{
                      paddingLeft: "12px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography align="left" style={{ textTransform: "none" }}>
                      {convertDateTimeFormat(data1.date)}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={2}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      textAlign: "left",
                    }}
                  >
                    <Typography style={{ textTransform: "none" }}>
                      {data1.deviceId}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      textAlign: "left",
                    }}
                  >
                    <Typography style={{ textTransform: "none" }}>
                      {userDetails?.[data1.userId]?.name || data1?.userId}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      textAlign: "left",
                    }}
                  >
                    <Tooltip title={data1.type || "Unknown"} arrow>
                      <img
                        src={getLogTypeIcon(data1.type)}
                        alt={data1.type}
                        style={{ width: 24, height: 24 }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid
                    item
                    xs={5}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      textAlign: "left",
                    }}
                  >
                    <Typography style={{ textTransform: "none" }}>
                      {data1.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Button>
            </CardCommon>
          ))}
        </Grid>
      </Grid>

      <LogDetailsModal
              isOpen={isOpenLogDetailsModal}
              setIsOpen={setIsOpenLogDetailsModal}
              selectedData={selectedLogDetails}
              userDetails={userDetails}
              locationSelectorList={locationSelectorList}
              isGeneric={isGeneric}
              userName={userName}
            />
    </>
  );
};

export default WithLoading(LogDetailsInfo);
